import type { ReactNode } from "react";
import type { Language, PriceOffer } from "../types";
import { formatDate, type Words } from "./copy";
import { fill, MAX_UNITS, type Bill, type BillIn } from "./multiBuy";
import { datedOffer, endsOnInstant, type DatedOffer } from "./offerWindow";

/** A size as an offer gives it. */
type Chip = NonNullable<PriceOffer["sizes"]>[number];

/** The pieces of the page that say how many, and what that comes to, where
 *  the page decides it rather than Stripe's form: the multi-buy, every extra
 *  piece in the same parcel at its own lower price (preview/multiBuy.ts).
 *  Used by the order sheet, "Order them together" and the price card, so the
 *  three say it the same way. */

/** Whether the page decides how many of a piece, for this offer: only where
 *  it takes the payment itself, and only when the server sent, for every
 *  size, the two prices the bill is made of and the format to write a total
 *  in. The server sends them only while the multi-buy applies to the page
 *  (CHECKOUT_MULTIBUY); without them the sheet is exactly what it always was,
 *  and how many is changed in Stripe's form. */
export function ownsQuantity(offer: PriceOffer | null | undefined): boolean {
  const sizes = offer?.sizes ?? [];
  return (
    !!offer?.format &&
    sizes.length > 0 &&
    sizes.every(
      (s) => typeof s.cents === "number" && typeof s.fullCents === "number" && typeof s.extraCents === "number"
    )
  );
}

/** What a sheet's "how many" is kept under for the rest of the visit
 *  (PageInfo.qtys). The page's own piece is one key whichever of its buttons
 *  opened the sheet, as they share one size and one sheet's worth of choices;
 *  a piece of several is its own, by its id, as each card has its own sheet.
 *
 *  The bar of a page that shows one piece of several at a time names its
 *  piece, and is kept under the key "Order them together" keeps that piece by
 *  (qtySel(pieceKey("", id)) in selection.ts, written out here because that
 *  file imports this one): so what they chose for the piece in its own sheet
 *  is what the together sheet opens on, and what is chosen for one piece is
 *  never read as the next one's when the page swaps them. */
export function qtyKey(from: string, pieceId: string | undefined): string {
  if (from === "piece") return `piece:${pieceId ?? ""}`;
  return pieceId !== undefined ? `sel::${pieceId}` : "page";
}

/** A number kept for the visit as the sheet uses it: one at the least and
 *  MAX_UNITS at the most, and one for nothing kept. */
export function unitsOf(qty: number | undefined): number {
  return Math.min(Math.max(1, Math.floor(qty ?? 1) || 1), MAX_UNITS);
}

/** A chosen size, as many times over, as the bill takes it. Only ever asked
 *  of a chip ownsQuantity() has passed, so the fallbacks are never used; they
 *  are what an extra piece would cost with nothing off it. */
export function billIn(chip: Chip, qty: number): BillIn {
  const cents = chip.cents ?? 0;
  return { fullCents: chip.fullCents ?? cents, cents, extraCents: chip.extraCents ?? cents, qty };
}

/** A piece in the bill, for its lines: what it is called, at which size and
 *  how many, its chip, its offer, and whether its lead holds a code. */
export interface BillPiece {
  key: string;
  name: string;
  size: string;
  chip: Chip;
  offer: PriceOffer;
  withCode: boolean;
  qty: number;
}

/** The bill's lines, one asked piece after another in the order the page
 *  sends them, from bill()'s own answer, so every amount here is one the
 *  server will charge. The dearest piece is its own price on its first line,
 *  with the code's percent beside it where its lead holds one, and its other
 *  copies on a line of their own; every other piece is one line at the extra
 *  price, "2 × " in front when there are two. Beside an extra price, the
 *  multi-buy's percent where it took something off, else the code's, which
 *  is then what the price holds: never both, as never both come off. */
export function billRows(pieces: BillPiece[], billed: Bill, format: string, t: Words): BillRow[] {
  return pieces.flatMap((piece, at) => {
    const line = billed.lines[at];
    if (!line) return [];
    const { chip, offer, qty } = piece;
    const cents = chip.cents ?? 0;
    const extra = Math.min(chip.extraCents ?? cents, cents);
    const codeTag = piece.withCode ? t.t("multiBuyOff", offVars(offer.percent ?? 30)) : null;
    const extraTag = extra < cents && offer.multiBuy ? t.t("multiBuyOff", offVars(offer.multiBuy.percent)) : codeTag;
    // Ruled off from the size: a name that ends in where the piece sits read
    // "Keychain · 1 of 2 4 cm", the two numbers run together.
    const what = `${piece.name}\u00a0· ${unbroken(piece.size)}`;
    if (!line.dearest) {
      return [{ key: piece.key, label: qty > 1 ? `${qty} × ${what}` : what, tag: extraTag, amount: fill(format, line.amount) }];
    }
    const first: BillRow = { key: piece.key, label: what, tag: codeTag, amount: chip.now };
    if (qty < 2) return [first];
    return [
      first,
      { key: `${piece.key}+`, label: `+${qty - 1} × ${what}`, tag: extraTag, amount: fill(format, (qty - 1) * extra) },
    ];
  });
}

/** A size or an amount as the new lines write it inside a sentence: "6 cm"
 *  or "24,74 €" never split over two lines between the number and the unit
 *  (the price card's offer line left "€." alone on a line of its own). */
export function unbroken(size: string): string {
  return size.replace(/ /g, " ");
}

/** What a percent off is filled into the copy with: `percent` as most
 *  languages say it ("−25%"), and `pay` for Chinese, which says a discount as
 *  what is paid, in tenths where it can: 25% off is 75 折, 30% off is 7 折. The
 *  same sum as the newsletter card's (NewsletterOffer.tsx). */
export function offVars(percent: number): { percent: string; pay: string } {
  const pay = 100 - percent;
  return { percent: String(percent), pay: String(pay % 10 === 0 ? pay / 10 : pay) };
}

/** "How many?", with − and + either side of the number. One at the least and
 *  `max` at the most, where + stops and the line under it says why. Only ever
 *  where they choose (the sheet's first step, the rows of "Order them
 *  together"), never beside Stripe's form: every new number there would be a
 *  new checkout session, and a page has only so many. */
export function QtyStepper({
  value,
  max,
  onChange,
  words: t,
  label,
}: {
  value: number;
  max: number;
  onChange: (next: number) => void;
  words: Words;
  /** What the group is called to a screen reader: "How many?", with the
   *  piece where there are several. */
  label: string;
}) {
  return (
    <div className="qty-stepper mono" role="group" aria-label={label}>
      <button
        type="button"
        aria-label={t.t("qtyLess")}
        disabled={value <= 1}
        onClick={() => onChange(Math.max(1, value - 1))}
      >
        −
      </button>
      <output aria-live="polite">{value}</output>
      <button
        type="button"
        aria-label={t.t("qtyMore")}
        disabled={value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
      >
        +
      </button>
    </div>
  );
}

/** One line of the bill: what it is, a small "−25%" (or the code's percent)
 *  beside it when something came off, and what it costs. */
export interface BillRow {
  key: string;
  label: string;
  tag?: string | null;
  amount: string;
}

/** The bill, line by line, under the sizes: the first piece at its own price,
 *  each extra one at its lower one, the total, and what one parcel saved.
 *  Only from two pieces on: for one there is nothing to add up, and the
 *  button says the price. The extra price is never struck through against
 *  the full one: it is a price of its own, not a reduction from a "was". */
export function BillBox({
  rows,
  total,
  save,
  words: t,
}: {
  rows: BillRow[];
  total: string;
  /** What the multi-buy took off, written as the total is; null when it
   *  took nothing (a code that already takes more). */
  save: string | null;
  words: Words;
}) {
  return (
    <div className="bill mono">
      {rows.map((row) => (
        <div className="bill-line" key={row.key}>
          <span>
            {row.label}
            {row.tag && (
              <>
                {" "}
                <span className="bill-off">{row.tag}</span>
              </>
            )}
          </span>
          <span>{row.amount}</span>
        </div>
      ))}
      <div className="bill-line bill-total" aria-live="polite">
        <span>{t.t("billTotal")}</span>
        <span>{total}</span>
      </div>
      {save && <p className="bill-save">{t.t("billSave", { amount: save })}</p>}
    </div>
  );
}

/** The offer: a "−25%" badge and a line about it, on the price card and on
 *  "Order them together". Never a struck price. Standing, it has no end
 *  date: it is how the shop prices a second piece, not a sale. Inside a
 *  dated window of the server's (the Christmas, Black Friday and Cyber Week
 *  offer, preview/offerWindow.ts) the badge is the window's "−35%", and the
 *  line under a title for the framing says the end date and, for whoever it
 *  is true of, what an extra piece usually costs ("habitualmente −25%"),
 *  still never struck through (multiBuyWords below). */
export function MultiBuyOffer({ badge, children }: { badge: string; children: ReactNode }) {
  return (
    <div className="multibuy-offer">
      <span className="multibuy-badge mono">{badge}</span>
      <div className="small">{children}</div>
    </div>
  );
}

/** What the page says about the multi-buy, wherever it says it: its title,
 *  the line under "How many?", the line on "Order them together", and the
 *  price card's "one more {size} for {price}".
 *
 *  Standing, the words it always had. Inside a dated window (`dated`), the
 *  title by the framing the server chose ("Natal: o 2.º MiniME −35%" up to
 *  the last day to order for Christmas, "Black Friday / Cyber Week: …"
 *  after), and every line ends on the window's last day, written long ("até
 *  2 de dezembro"), with "habitualmente −25%" and the extra piece's usual
 *  price beside the window's ones where the server sent them: never for a
 *  lead whose own code takes as much off as the usual percentage
 *  (FIRSTORDER30), for whom it would not be true. Chinese says each discount
 *  as the share that is paid, 65 折 and 75 折, and puts an aside in
 *  full-width brackets. */
export function multiBuyWords(multi: NonNullable<PriceOffer["multiBuy"]>, t: Words, language: Language) {
  const off = offVars(multi.percent);
  const dated: DatedOffer | null = datedOffer(multi);
  if (!dated) {
    return {
      dated,
      title: t.t("multiBuyTitle", off),
      each: t.t("multiBuyEach", off),
      together: t.t("multiBuyTogether", off),
      card: (size: string, price: string, _usual?: string) => t.t("multiBuyCard", { size, price }),
    };
  }
  const chinese = language === "zh" || language === "zh-hans";
  const date = formatDate(endsOnInstant(dated.endsOn), language);
  const usualOff =
    dated.usual === null
      ? ""
      : aside(t.t("multiBuyUsualOff", { usual: String(dated.usual), usualPay: offVars(dated.usual).pay }), chinese);
  const title =
    dated.frame === "christmas" ? "multiBuyTitleXmas" : dated.frame === "late" ? "multiBuyTitleBF" : "multiBuyTitle";
  return {
    dated,
    title: t.t(title, off),
    each: t.t("multiBuyEachPromo", { ...off, usualNote: usualOff, date }),
    together: t.t("multiBuyTogetherPromo", { ...off, usualNote: usualOff, date }),
    /** One more of a size at the window's price, and what it usually costs
     *  when the server said (`usual`, sizes[].usualExtra). */
    card: (size: string, price: string, usual?: string) => {
      const said = usual && dated.usual !== null ? t.t("multiBuyUsualPrice", { usual }) : "";
      const usualNote = !said ? "" : chinese ? `（${said}）` : ` · ${said}`;
      return t.t("multiBuyCardPromo", { size, price, usualNote, date });
    },
  };
}

/** A few words beside what they qualify: " (habitualmente −25%)", or in
 *  Chinese "（平時 75 折）", which takes no space. */
function aside(text: string, chinese: boolean): string {
  return chinese ? `（${text}）` : ` (${text})`;
}
